<template>
  <Combobox
    ref="combobox"
    multiple
    chip
    clearable
    :addingAllowed="addingAllowed"
    :modelValue="selectedIds"
    :items="availableTags"
    :getLabel="tag => tag.name"
    :disabled="disabled"
    :readonly="readonly"
    :panelClass="panelClass"
    :placeholder="placeholder || t('addTask.search_tags')"
    @update:modelValue="onSelectionChange"
    @create="confirmNewTag"
  >
    <template #selected="{ items, remove }">
      <div
        v-for="tag in items"
        :key="tag.id"
        class="tag-chip tag-tint"
        :class="{ 'cursor-auto': readonly }"
        :style="{ '--tag-color': `#${tag.color}` }"
        :title="tag.name"
        @click.stop="readonly || remove(tag.id)"
      >
        <span class="truncate">{{ tag.name }}</span>
        <i v-if="!readonly" class="tr tr-close text-[0.5rem]"></i>
      </div>
    </template>

    <template #item="{ item }">
      <div
        class="tag-chip tag-tint"
        :style="{ '--tag-color': `#${item.color}` }"
        :title="item.name"
      >
        <span class="truncate">{{ item.name }}</span>
      </div>
    </template>

    <template #empty="{ query }">
      <div class="flex min-h-7.5 items-center justify-center text-label text-muted">
        {{ emptyMessage(query) }}
      </div>
    </template>

    <!-- offered whenever the typed name is new, even alongside partial matches -->
    <template #footer="{ query }">
      <NewTagOption
        v-if="addingAllowed && query && !nameTaken(query)"
        ref="newTagOption"
        :name="query"
        @create="create"
      />
    </template>
  </Combobox>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Combobox from '@/components/elements/Combobox.vue';
import NewTagOption from '@/components/widgets/NewTagOption.vue';

const props = defineProps({
  // the picked tags themselves, so the host keeps working with tag objects
  tags: {
    type: Array,
    default: () => [],
  },
  // every tag of the project; Combobox drops the picked ones from the suggestions
  availableTags: {
    type: Array,
    default: () => [],
  },
  disabled: Boolean,
  readonly: Boolean,
  panelClass: {
    type: String,
    default: '',
  },
  // off where the combobox only picks among existing tags, e.g. a report filter
  addingAllowed: {
    type: Boolean,
    default: true,
  },
  placeholder: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:tags', 'create']);

const { t } = useI18n();

const combobox = ref(null);
const newTagOption = ref(null);

const selectedIds = computed(() => props.tags.map(tag => tag.id));

const nameTaken = name => props.availableTags
  .some(tag => tag.name.toLowerCase() === name.toLowerCase());

// only reached with no suggestions listed, so Enter does create the typed name
function emptyMessage(query) {
  if (!query) {
    // the "no tags yet" hint reads as an invitation, so it belongs where creating is offered
    const empty = !props.availableTags.length && props.addingAllowed;
    return empty ? t('addTask.no_tags_hint') : t('addTask.no_tags');
  }
  if (!props.addingAllowed || nameTaken(query)) return t('addTask.nothing_found');
  return t('combobox.create_hint', { query });
}

function onSelectionChange(ids) {
  const byId = new Map(props.availableTags.map(tag => [tag.id, tag]));
  emit('update:tags', ids.map(id => byId.get(id)));
}

// Enter reaches the combobox, not the create option, so it is bridged across
function confirmNewTag() {
  if (!newTagOption.value) return;
  newTagOption.value.confirm();
}

function create(tag) {
  emit('create', tag);
  combobox.value.settle();
}
</script>
